import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { signIn, signOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { useIdleTimer } from 'react-idle-timer';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';

import { SRP_AUTH_FLOW } from '../../../config/amplify';
import {
  DEEPLINK_URL_PARAM,
  HOME_ROUTE,
  LOGIN_PROVIDER_CLIENTS_PORTAL,
  LOGIN_PROVIDER_CREDENTIALS,
  LOGIN_PROVIDER_GOOGLE,
  LOGIN_PROVIDER_MICROSOFT,
  LOGIN_PROVIDER_NAME,
  LOGIN_ROUTE,
  SESSION_INACTIVITY_TIMEOUT
} from '../../constants/auth';
import { BETTERFLY_DOMAIN, GOBETTERFLY_DOMAIN, IS_STG } from '../../constants/constants';
import {
  CLICK_ACTION,
  IDENTIFY_EVENT,
  LOGIN,
  LOGOUT,
  USER_ATTRIBUTES_IDENTIFIER,
  SET_ACTION,
  STATE_ERROR,
  STATE_SUCCESSFUL,
  CLIENTS_PORTAL_PROVIDER,
  GOOGLE_PROVIDER
} from '../../constants/tagManager';
import { ISessionContext, IUserProfile } from '../../interfaces/auth';
import Users from '../../services/api/users/users';
import TagManagerService from '../../services/gtm/tagManagerService';
import { GenericObject } from '../../types/genericTypes';
import { isAppRoute, removePlusTextFromEmail } from '../../utils/auth/auth';
import {
  getAndRemoveLocalStorage,
  getFromLocalStorage,
  removeFromLocalStorage,
  toLocalStorage
} from '../../utils/utils';

const contextDefaultValues: ISessionContext = {
  userId: '',
  isLoggedIn: false,
  verifyingLogin: false,
  errorCode: '',
  email: '',
  user: {
    name: '',
    lastName: '',
    email: '',
    phone: {
      number: ''
    },
    image: ''
  },
  isBetterflyer: false,
  sessionLogin: async () => {
    return;
  },
  sessionLogout: async () => {
    return;
  },
  resetError: () => {
    return;
  },
  setEmail: () => {
    return;
  },
  setUserAttributes: () => {
    return;
  },
  socialLogin: async () => {
    return;
  },
  customLogin: async () => {
    return;
  }
};

export const SessionContext = createContext<ISessionContext>(contextDefaultValues);

const SessionContextProvider = ({ children }: React.PropsWithChildren) => {
  const [userId, setUserId] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [verifyingLogin, setVerifyingLogin] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [errorCode, setErrorCode] = useState('');
  const { shutdown, boot } = useIntercom();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<IUserProfile | GenericObject>({});
  const [isBetterflyer, setIsBetterflyer] = useState(false);

  const handleOnIdle = () => {
    if (isLoggedIn) {
      sessionLogout();
    }
  };

  useIdleTimer({
    timeout: SESSION_INACTIVITY_TIMEOUT,
    onIdle: handleOnIdle
  });

  const sessionLogin = useCallback(
    async (userEmail: string, password: string, recaptchaToken: string) => {
      try {
        const sessionData = await signIn({
          username: userEmail,
          password,
          options: { clientMetadata: { recaptchaToken } }
        });
        if (sessionData.isSignedIn) {
          await Users.notifyLogin(LOGIN_PROVIDER_CREDENTIALS);
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken;
          let adminEmail = userEmail;
          let userId = '';
          if (idToken && typeof idToken === 'object' && idToken.payload) {
            adminEmail = idToken.payload.email ? String(idToken.payload.email) : userEmail;
            userId = idToken.payload.sub ? String(idToken.payload.sub) : '';
          }
          setErrorCode('');
          toLocalStorage(LOGIN_PROVIDER_NAME, LOGIN_PROVIDER_CLIENTS_PORTAL);
          setIsLoggedIn(true);
          setEmail(adminEmail);
          setUserId(userId);
          setIsBetterflyer(adminEmail.endsWith(BETTERFLY_DOMAIN) || adminEmail.endsWith(GOBETTERFLY_DOMAIN));
          TagManagerService.pushEvent(CLICK_ACTION, LOGIN, { ...STATE_SUCCESSFUL, provider: CLIENTS_PORTAL_PROVIDER });
          TagManagerService.pushEvent(SET_ACTION, IDENTIFY_EVENT, {
            identifier: USER_ATTRIBUTES_IDENTIFIER.concat(userId)
          });
          return redirectToApp();
        }
      } catch (error: unknown) {
        TagManagerService.pushEvent(CLICK_ACTION, LOGIN, { ...STATE_ERROR });
        setErrorCode(error as React.SetStateAction<string>);
      }
    },
    [navigate]
  );

  const sessionLogout = useCallback(async () => {
    try {
      SRP_AUTH_FLOW();
      const provider = getAndRemoveLocalStorage(LOGIN_PROVIDER_NAME) ?? LOGIN_PROVIDER_CLIENTS_PORTAL;
      shutdown();
      setErrorCode('');
      TagManagerService.pushEvent(CLICK_ACTION, LOGOUT, STATE_SUCCESSFUL);
      await signOut();
      if (provider == LOGIN_PROVIDER_CLIENTS_PORTAL || provider == LOGIN_PROVIDER_MICROSOFT) {
        setIsLoggedIn(false);
        navigate(LOGIN_ROUTE);
      }
      localStorage.removeItem(DEEPLINK_URL_PARAM);
    } catch {
      TagManagerService.pushEvent(CLICK_ACTION, LOGOUT, STATE_ERROR);
    } finally {
      boot({ hideDefaultLauncher: false });
    }
  }, [navigate, shutdown]);

  const socialLogin = useCallback(
    async ({ email = '', sub = '' }: { email?: string; sub?: string }) => {
      try {
        setVerifyingLogin(true);
        await Users.notifyLogin(LOGIN_PROVIDER_GOOGLE);
        const adminEmail = email;
        const userId = sub || '';
        toLocalStorage(LOGIN_PROVIDER_NAME, LOGIN_PROVIDER_GOOGLE);
        setUserId(userId);
        setIsLoggedIn(true);
        setEmail(adminEmail);
        setIsBetterflyer(adminEmail.endsWith(BETTERFLY_DOMAIN) || adminEmail.endsWith(GOBETTERFLY_DOMAIN));
        TagManagerService.pushEvent(CLICK_ACTION, LOGIN, { ...STATE_SUCCESSFUL, provider: GOOGLE_PROVIDER });
        TagManagerService.pushEvent(SET_ACTION, IDENTIFY_EVENT, {
          identifier: USER_ATTRIBUTES_IDENTIFIER.concat(userId)
        });
        redirectToApp();
      } catch {
        setIsLoggedIn(false);
      } finally {
        setVerifyingLogin(false);
      }
    },
    [navigate]
  );

  const customLogin = useCallback(
    async (token: { attributes: { email?: string; sub?: string } }, provider: string, gtmProvider: string) => {
      try {
        setVerifyingLogin(true);
        await Users.notifyLogin(provider);
        const userId = token.attributes.sub || '';
        const adminEmail = token.attributes.email || '';
        toLocalStorage(LOGIN_PROVIDER_NAME, provider);
        setUserId(userId);
        setIsLoggedIn(true);
        setEmail(removePlusTextFromEmail(adminEmail));
        setIsBetterflyer(adminEmail.endsWith(BETTERFLY_DOMAIN) || adminEmail.endsWith(GOBETTERFLY_DOMAIN));
        TagManagerService.pushEvent(CLICK_ACTION, LOGIN, { ...STATE_SUCCESSFUL, provider: gtmProvider });
        TagManagerService.pushEvent(SET_ACTION, IDENTIFY_EVENT, {
          identifier: USER_ATTRIBUTES_IDENTIFIER.concat(userId)
        });
        redirectToApp();
      } catch {
        setIsLoggedIn(false);
      } finally {
        setVerifyingLogin(false);
      }
    },
    [navigate]
  );

  const resetError = useCallback((err: string) => {
    setErrorCode(err);
  }, []);

  const hideRecaptcha = () => {
    (document.getElementsByClassName('grecaptcha-badge') as HTMLCollectionOf<HTMLElement>)[0].style.display = 'None';
  };

  const redirectToApp = (): void => {
    const redirectionPath = getFromLocalStorage(DEEPLINK_URL_PARAM);
    if (redirectionPath) {
      removeFromLocalStorage(DEEPLINK_URL_PARAM);
      navigate(redirectionPath);
    } else {
      navigate(HOME_ROUTE);
    }
  };

  const setUserAttributes = useCallback(
    (personalData: GenericObject) => {
      setUser({
        ...user,
        ...personalData
      });
    },
    [user]
  );

  useEffect(() => {
    async function userLoggedIn() {
      setVerifyingLogin(true);
      if (IS_STG) {
        hideRecaptcha();
      }
      try {
        await getCurrentUser();
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;
        let adminEmail = '';
        let userId = '';
        if (idToken && typeof idToken === 'object' && idToken.payload) {
          adminEmail = typeof idToken.payload.email === 'string' ? idToken.payload.email : '';
          userId = typeof idToken.payload.sub === 'string' ? idToken.payload.sub : '';
        }
        setUserId(userId);
        setIsLoggedIn(true);
        setEmail(removePlusTextFromEmail(adminEmail));
        setIsBetterflyer(adminEmail.endsWith(BETTERFLY_DOMAIN) || adminEmail.endsWith(GOBETTERFLY_DOMAIN));
        if (!isAppRoute(location.pathname)) {
          navigate(HOME_ROUTE);
        }
      } catch {
        removeFromLocalStorage(LOGIN_PROVIDER_NAME);
        setIsLoggedIn(false);
      } finally {
        setVerifyingLogin(false);
      }
    }

    userLoggedIn();
  }, [navigate, location]);

  useEffect(() => {
    boot({ hideDefaultLauncher: false });
  }, []);

  const value = useMemo(
    () => ({
      userId,
      isLoggedIn,
      verifyingLogin,
      errorCode,
      email,
      user,
      isBetterflyer,
      sessionLogin,
      sessionLogout,
      resetError,
      setEmail,
      setUserAttributes,
      socialLogin,
      customLogin
    }),
    [
      userId,
      isLoggedIn,
      verifyingLogin,
      errorCode,
      email,
      user,
      isBetterflyer,
      sessionLogin,
      sessionLogout,
      resetError,
      setEmail,
      setUserAttributes,
      socialLogin,
      customLogin
    ]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export default SessionContextProvider;
